import React from 'react';
import {
  hasAllocationData,
} from 'pages/Dashboard/pages/Billing/Payments/utils';
import {
  CellContext,
} from '@tanstack/react-table';
import {
  ServiceAllocation,
} from 'pages/Dashboard/pages/Billing/types';
import {
  useNotify,
} from 'core/hooks';
import DropdownMenu from 'core/components/DropdownMenu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useExcludeServiceLine,
} from 'pages/Dashboard/pages/Billing/hooks';
import compact from 'lodash/compact';
import {
  isServiceLineInactive,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/serviceLineStatus';

const SYSTEM_MANAGED_EXCLUDE_TOOLTIP =
  'This system-managed EOB service line cannot be manually excluded.';

export default function EOBServiceCodeActionCell({ row }: CellContext<ServiceAllocation, unknown>) {
  const {
    claim,
    id,
    isExcluded,
    canManuallyExclude,
    eobServiceCodePaymentInfoId = 0,
  } = row.original ?? {};
  const isInactive = isServiceLineInactive(row.original);
  const disableClear = !hasAllocationData([row.original]) || isInactive;
  const { mutateAsync: excludeServiceLine } = useExcludeServiceLine();

  const notify = useNotify();

  const handleClear = () => {
    paymentsEventChannel.emit(
      'onEOBAllocationDelete',
      claim?.claimId ?? 0,
      id ?? 0,
    );
  };

  const handleExclude = async () => {
    const excluded = !isExcluded;

    try {
      await excludeServiceLine({ eobServiceCodePaymentInfoId, isExcluded: excluded });

      notify({
        message: (
          <div className="flex gap-1">
            <span className="font-semibold">
              Service:
              {row?.original?.serviceName}
            </span>
            <span>{excluded ? 'excluded from' : 'included in'}</span>
            <span>payment calculation.</span>
          </div>
        ),
        variant: 'success',
      });
      paymentsEventChannel.emit(
        'onEOBAllocationExclude',
        row.original?.claim?.claimId ?? 0,
        row.original?.id ?? 0,
        excluded,
      );
    } catch (error) {
      notify({
        message: 'Failed to exclude service line',
        variant: 'error',
      });
    }
  };

  const excludeLabel = isExcluded
    ? 'Undo Exclude'
    : 'Exclude';

  const disableExclude = !isExcluded && !canManuallyExclude;

  const items = compact([
    {
      id: 'clearAllItems',
      label: 'Clear all items',
      disabled: disableClear,
      onSelect: handleClear,
    },
    eobServiceCodePaymentInfoId === 0 ? undefined : {
      id: 'excludeServiceLine',
      label: excludeLabel,
      disabled: disableExclude,
      tooltip: disableExclude ? SYSTEM_MANAGED_EXCLUDE_TOOLTIP : undefined,
      onSelect: handleExclude,
    },
  ]);
  return (
    <div className="flex items-center justify-center !w-full">
      <DropdownMenu
        trigger={(
          <button
            type="button"
            aria-label="more"
            className="inline-flex h-6 w-6 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
          >
            <MoreVertIcon className="!h-5 !w-5" />
          </button>
        )}
        placement="bottom-end"
        openOnHover
        items={items}
      />
    </div>
  );
}
